import { supabase } from '@/lib/supabase-client';
class WorkspaceMemberService {
    supabaseClient = supabase;
    /**
     * Get all members of a workspace
     */
    async getWorkspaceMembers(workspaceId) {
        try {
            const { data, error } = await this.supabaseClient
                .from('workspace_members')
                .select(`
          id,
          workspace_id,
          user_id,
          role,
          permissions,
          joined_at,
          invited_by,
          invitation_accepted_at,
          created_at,
          updated_at
        `)
                .eq('workspace_id', workspaceId)
                .order('joined_at', { ascending: false });
            if (error)
                throw error;
            // Fetch user details for each member
            const membersWithDetails = await Promise.all((data || []).map(async (member) => {
                const { data: userData } = await this.supabaseClient
                    .from('profiles')
                    .select('email, full_name')
                    .eq('id', member.user_id)
                    .single();
                return {
                    id: member.id,
                    userId: member.user_id,
                    workspaceId: member.workspace_id,
                    role: member.role,
                    email: userData?.email,
                    fullName: userData?.full_name,
                    permissions: member.permissions,
                    joinedAt: new Date(member.joined_at),
                    invitedBy: member.invited_by,
                    invitationAcceptedAt: member.invitation_accepted_at ? new Date(member.invitation_accepted_at) : undefined,
                    createdAt: new Date(member.created_at),
                    updatedAt: new Date(member.updated_at),
                };
            }));
            return membersWithDetails;
        }
        catch (error) {
            console.error('Error fetching workspace members:', error);
            throw error;
        }
    }
    /**
     * Add a member to a workspace
     */
    async addMember(workspaceId, userId, role = 'member', invitedBy) {
        try {
            const { data, error } = await this.supabaseClient
                .from('workspace_members')
                .insert([
                {
                    workspace_id: workspaceId,
                    user_id: userId,
                    role,
                    invited_by: invitedBy,
                    permissions: this.getPermissionsForRole(role),
                },
            ])
                .select()
                .single();
            if (error)
                throw error;
            // Fetch user details
            const { data: userData } = await this.supabaseClient
                .from('profiles')
                .select('email, full_name')
                .eq('id', userId)
                .single();
            return {
                id: data.id,
                userId: data.user_id,
                workspaceId: data.workspace_id,
                role: data.role,
                email: userData?.email,
                fullName: userData?.full_name,
                permissions: data.permissions,
                joinedAt: new Date(data.joined_at),
                invitedBy: data.invited_by,
                invitationAcceptedAt: data.invitation_accepted_at ? new Date(data.invitation_accepted_at) : undefined,
                createdAt: new Date(data.created_at),
                updatedAt: new Date(data.updated_at),
            };
        }
        catch (error) {
            console.error('Error adding workspace member:', error);
            throw error;
        }
    }
    /**
     * Update a member's role in a workspace
     */
    async updateMemberRole(workspaceMemberId, role) {
        try {
            const { data, error } = await this.supabaseClient
                .from('workspace_members')
                .update({
                role,
                permissions: this.getPermissionsForRole(role),
                updated_at: new Date().toISOString(),
            })
                .eq('id', workspaceMemberId)
                .select()
                .single();
            if (error)
                throw error;
            // Fetch user details
            const { data: userData } = await this.supabaseClient
                .from('profiles')
                .select('email, full_name')
                .eq('id', data.user_id)
                .single();
            return {
                id: data.id,
                userId: data.user_id,
                workspaceId: data.workspace_id,
                role: data.role,
                email: userData?.email,
                fullName: userData?.full_name,
                permissions: data.permissions,
                joinedAt: new Date(data.joined_at),
                invitedBy: data.invited_by,
                invitationAcceptedAt: data.invitation_accepted_at ? new Date(data.invitation_accepted_at) : undefined,
                createdAt: new Date(data.created_at),
                updatedAt: new Date(data.updated_at),
            };
        }
        catch (error) {
            console.error('Error updating workspace member role:', error);
            throw error;
        }
    }
    /**
     * Remove a member from a workspace
     */
    async removeMember(workspaceMemberId) {
        try {
            const { error } = await this.supabaseClient
                .from('workspace_members')
                .delete()
                .eq('id', workspaceMemberId);
            if (error)
                throw error;
        }
        catch (error) {
            console.error('Error removing workspace member:', error);
            throw error;
        }
    }
    /**
     * Check if user is member of workspace
     */
    async isMember(workspaceId, userId) {
        try {
            const { data, error } = await this.supabaseClient
                .from('workspace_members')
                .select('id')
                .eq('workspace_id', workspaceId)
                .eq('user_id', userId)
                .single();
            return !error && !!data;
        }
        catch {
            return false;
        }
    }
    /**
     * Get member's role in a workspace
     */
    async getMemberRole(workspaceId, userId) {
        try {
            const { data, error } = await this.supabaseClient
                .from('workspace_members')
                .select('role')
                .eq('workspace_id', workspaceId)
                .eq('user_id', userId)
                .single();
            if (error)
                return null;
            return data?.role || null;
        }
        catch {
            return null;
        }
    }
    /**
     * Get default permissions for a role
     */
    getPermissionsForRole(role) {
        const permissions = {
            owner: {
                canEdit: true,
                canDelete: true,
                canInvite: true,
                canManageMembers: true,
            },
            admin: {
                canEdit: true,
                canDelete: true,
                canInvite: true,
                canManageMembers: true,
            },
            editor: {
                canEdit: true,
                canDelete: false,
                canInvite: false,
                canManageMembers: false,
            },
            member: {
                canEdit: true,
                canDelete: false,
                canInvite: false,
                canManageMembers: false,
            },
            viewer: {
                canEdit: false,
                canDelete: false,
                canInvite: false,
                canManageMembers: false,
            },
        };
        return permissions[role] || permissions.member;
    }
    /**
     * Invite a user by email to workspace
     */
    async inviteMemberByEmail(workspaceId, email, role, invitedBy) {
        try {
            // Find user by email
            const { data: userData, error: userError } = await this.supabaseClient
                .from('profiles')
                .select('id')
                .eq('email', email)
                .single();
            if (userError || !userData) {
                return {
                    success: false,
                    message: `User with email ${email} not found`,
                };
            }
            // Check if already a member
            const isMemberAlready = await this.isMember(workspaceId, userData.id);
            if (isMemberAlready) {
                return {
                    success: false,
                    message: `User is already a member of this workspace`,
                };
            }
            // Add member
            await this.addMember(workspaceId, userData.id, role, invitedBy);
            return {
                success: true,
                message: `Successfully invited ${email} to workspace`,
            };
        }
        catch (error) {
            console.error('Error inviting member by email:', error);
            return {
                success: false,
                message: `Failed to invite member: ${error instanceof Error ? error.message : 'Unknown error'}`,
            };
        }
    }
}
export const workspaceMemberService = new WorkspaceMemberService();
